import { useCallback, useEffect, useRef } from 'react';
import { SearchProvider } from '../../contexts/SearchProvider';
import shopify from '../../../js/shopify';
import helpers from '../../../js/helpers';
import api from '../../../js/api';

import COLLECTION_QUERY from '../../../js/graphql/2024_01/search/collection.graphql';
import COLLECTION_SIMPLE_QUERY from '../../../js/graphql/2024_01/search/collection_count.graphql';
import SEARCH_QUERY from '../../../js/graphql/2024_01/search/search.graphql';
import PREDICTIVE_SEARCH_QUERY from '../../../js/graphql/2024_01/search/predictive-search.graphql';
import { isObjectType } from 'graphql';

/**
 * Utility Functions
 */

/**
 * Debounce helper function to limit how often a function can be called
 * @param {Function} func - The function to debounce
 * @param {number} wait - The debounce delay in milliseconds
 * @return {Function} The debounced function
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * URL Parameter Management
 */

/**
 * Convert filters object to URL search parameters
 * @param {Object} filtersObject - The filters object to convert
 * @returns {URLSearchParams} URL search parameters
 */
function filtersToURLParams(filtersObject) {
  const params = new URLSearchParams(window.location.search);
  const keysToRemove = [];
  for (const [key] of params) {
    if (key.startsWith('filter.')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => params.delete(key));
  Object.entries(filtersObject).forEach(([filterKey, values]) => {
    if (Array.isArray(values) && values.length > 0) {
      values.forEach((value) => params.append(`filter.${filterKey}`, value));
    }
  });
  return params;
}

/**
 * Parse URL search parameters to filters object
 * @param {URLSearchParams} params - URL search parameters
 * @returns {Object} Filters object
 */
function urlParamsToFilters(params) {
  const filters = {};
  let filterIndex = 0; // Counter to create unique keys for multiple values
  
  for (const [key, value] of params) {
    if (key.startsWith('filter.')) {
      const filterKey = key.replace('filter.', '');
      const keyData = filterKey.split('.');
      
      // Create a unique key for each filter value to avoid overwriting
      let uniqueFilterKey = filterKey;
      
      if (value.match('Taxonomy')) {
        // Check if we already have a filter with this base key
        const existingKeys = Object.keys(filters).filter(k => k.startsWith(filterKey));
        if (existingKeys.length > 0) {
          uniqueFilterKey = `${filterKey}_${filterIndex++}`;
        }
        
        filters[uniqueFilterKey] = {
          type: 'taxonomyMetafield',
          key: keyData[4],
          namespace: keyData[3],
          value: value
        };
      } else if (key.match('p.m')) {
        // Check if we already have a filter with this base key
        const existingKeys = Object.keys(filters).filter(k => k.startsWith(filterKey));
        if (existingKeys.length > 0) {
          uniqueFilterKey = `${filterKey}_${filterIndex++}`;
        }
        
        filters[uniqueFilterKey] = {
          type: 'productMetafield',
          key: keyData[4],
          namespace: keyData[3],
          value: value
        };
      } else if (key.match('p.t')) {
        // Check if we already have a filter with this base key
        const existingKeys = Object.keys(filters).filter(k => k.startsWith(filterKey));
        if (existingKeys.length > 0) {
          uniqueFilterKey = `${filterKey}_${filterIndex++}`;
        }

        filters[uniqueFilterKey] = {
          type: 'category',
          key: 'category',
          namespace: null,
          id: value
        };
      } else if (key.match('v.option')) {
        // Check if we already have a filter with this base key
        const existingKeys = Object.keys(filters).filter(k => k.startsWith(filterKey));
        if (existingKeys.length > 0) {
          uniqueFilterKey = `${filterKey}_${filterIndex++}`;
        }

        filters[uniqueFilterKey] = {
          type: 'variantOption',
          key: keyData[3],
          namespace: null,
          value: value
        };
      } else if (key.match('price')) {
        filters[uniqueFilterKey] = {
          type: 'price',
          key: 'price',
          namespace: null,
          value: {
            min: parseFloat(value.split('-')[0]),
            max: parseFloat(value.split('-')[1])
          }
        };
      } else {
        // Check if we already have a filter with this base key
        const existingKeys = Object.keys(filters).filter(k => k.startsWith(filterKey));
        if (existingKeys.length > 0) {
          uniqueFilterKey = `${filterKey}_${filterIndex++}`;
        }
        
        filters[uniqueFilterKey] = {
          key: filterKey,
          namespace: null,
          value: value
        };
      }
    }
  }
  return filters;
}

/**
 * Update URL with current filters without page reload
 * @param {Object} filtersObject - The filters object to sync to URL
 */
function updateURLWithFilters(filtersObject) {
  const params = filtersToURLParams(filtersObject);
  const newURL = `${window.location.pathname}?${params.toString()}`;
  window.history.pushState({ filters: filtersObject }, '', newURL);
}

/**
 * Get initial filters from URL on page load
 * @returns {Object} Filters object parsed from URL
 */
function getInitialFiltersFromURL() {
  const params = new URLSearchParams(window.location.search);
  return urlParamsToFilters(params);
}

/**
 * Filter Handling
 */

/**
 * Helper function to handle legacy filter format for URL initialization
 * @param {string} key - Filter key
 * @param {string} value - Filter value
 * @param {Array} filters - Array to push filters to
 */
function handleLegacyFilterForURL(key, value, filters) {
  if (key === 'Room') {
    filters.push({ tag: value });
  } else if (key === 'Category') {
    filters.push({
      productMetafield: {
        namespace: 'shopify',
        key: 'category',
        value: value
      }
    });
  } else if (key === 'Price' || key.includes('price')) {
    try {
      if (Array.isArray(value)) {
        const [min, max] = value[0].split('-');
        filters.push({ price: { min: parseFloat(min), max: parseFloat(max) } });
      } else {
        const [min, max] = value.split('-');
        filters.push({ price: { min: parseFloat(min), max: parseFloat(max) } });
      }
    } catch (e) {
      console.error(`Failed to parse price filter: ${value}`, e);
    }
  } else if (key === 'Brand') {
    filters.push({ productVendor: value });
  } else if (key === 'Product type') {
    filters.push({ productType: value });
  } else if (key === 'Availability') {
    filters.push({ available: value === 'In stock' });
  } else if (key.startsWith('named_tags.')) {
    filters.push({ tag: `${key.replace('named_tags.', '')}:${value}` });
  } else if (key.startsWith('options.')) {
    filters.push({
      variantOption: { name: key.replace('options.', ''), value: value }
    });
  } else {
    filters.push({ tag: value });
  }
}

/**
 * GraphQL Filter Conversion
 */

/**
 * Convert filters object to GraphQL format
 * @param {Object} filtersObject - The filters object to convert
 * @returns {Array} Array of GraphQL filter objects
 */
function convertFiltersObjectToGraphQL(filtersObject) {
  const filters = [];
  Object.entries(filtersObject).forEach(([key, filterData]) => {
 
    if (filterData.type === 'taxonomyMetafield' || filterData.type === 'productMetafield') {
      // Each filter object has a single value, not an array
      filters.push({
        [filterData.type]: {
          namespace: filterData.namespace,
          key: filterData.key,
          value: filterData.value
        }
      });
    } else if (filterData.type === 'price') {
      // Handle price filters
      filters.push({
        price: filterData.value
      });
    } else if (filterData.type === 'category') {
      // Handle category filters
      filters.push({
        [filterData.type]: {
          id: filterData.id
        }
      });
    } else if (filterData.type === 'variantOption') {
      // Handle variantOption filters
      filters.push({
        [filterData.type]: {
          name: filterData.key,
          value: filterData.value
        }
      });
    } else {
      // Handle other filter types
      filters.push({
        [filterData.key]: filterData.value
      });
    }
  });
  return filters;
}

/**
 * Creates a provider for Shopify search implementation
 *
 * @param {Object} props - The props for the ShopifySearchProvider component
 * @param {React.ReactNode} props.children - Child components
 * @param {Object} props.settings - Settings for the Shopify search
 * @param {string} props.searchType - Type of search ('search', 'plp', 'header')
 * @returns {JSX.Element} A SearchProvider with Shopify-specific implementation
 */
export function ShopifySearchProvider({ children, settings, searchType = 'search' }) {
  // Global guard to prevent multiple initializations
  if (!window.__shopifySearchProviderGlobal) {
    window.__shopifySearchProviderGlobal = {
      initialized: false,
      eventListenersSet: false
    };
  }
  const globalProviderState = window.__shopifySearchProviderGlobal;

  // Create refs to maintain state across re-renders
  const providerRef = useRef({
    initialized: false,
    currentSort: settings?.search_settings?.default_sort || settings?.default_sort || 'MANUAL',
    currentDirection: settings?.search_settings?.default_sort_direction || settings?.default_sort_direction || 'DESC',
    previousSort: settings?.search_settings?.default_sort || settings?.default_sort || 'MANUAL',
    previousDirection: settings?.search_settings?.default_sort_direction || settings?.default_sort_direction || 'DESC',
    currentFilters: [],
    currentPage: 1,
    currentCollectionSize: 0,
    currentCollection: settings?.collection?.handle || null,
    lastCursor: null,
    pageCursors: {}, // Store cursors for each page
    currentResults: 0, 
    searchQuery: settings?.query,
    previousSearchQuery: '',
    productsPerPage: settings?.search_settings?.products_per_page || 24,
    isProcessingRequest: false,
    debouncedSearchRequest: null,
    apiKey: settings?.api_key || null,
    configuredFacets: settings?.facets?.split('\n').filter(Boolean) || [],
    sortOptions: parseSortOptions(settings?.sort_options),
    hideOutOfStock: settings?.filters_hide_out_of_stock || false,
    isSortingActive: false
  });

  const provider = providerRef.current;

  /**
   * Parse sort options from the settings
   * Format: "RELEVANCE:Relevance\nCREATED:Newest\nBEST_SELLING:Popular\nPRICE_ASC:Price - Low to High\nPRICE_DESC:Price - High to Low\nMANUAL:Featured"
   * @param {string} sortOptionsStr - Sort options string from settings
   * @returns {Array} Array of sort option objects
   */
  function parseSortOptions(sortOptionsStr) {
    if (!sortOptionsStr) return [];

    return sortOptionsStr
      .split('\n')
      .filter(Boolean)
      .map((option) => {
        const [key, label] = option.split(':');
        // Handle special cases for price sorting which combines sort key and direction
        if (key === 'PRICE_ASC') {
          return { key: 'PRICE', direction: 'ASC', label };
        } else if (key === 'PRICE_DESC') {
          return { key: 'PRICE', direction: 'DESC', label };
        } else {
          return { key, label };
        }
      });
  }

  // Initialize the provider
  const initialize = useCallback((searchSettings) => {

    // Global guard: only allow one initialization
    if (globalProviderState.initialized) {
      // console.warn('ShopifySearchProvider: Already initialized globally, skipping duplicate initialization.');
      return;
    }
    globalProviderState.initialized = true;
    provider.initialized = true;

    // Set default values from settings
    provider.productsPerPage = searchSettings?.search_settings.products_per_page || 24;
    provider.currentSort = searchSettings?.default_sort || 'RELEVANCE';
    provider.currentDirection = searchSettings?.default_sort_direction || 'DESC';
    provider.apiKey = searchSettings?.api_key || api.getStorefrontToken();
    provider.configuredFacets = searchSettings?.facets?.split('\n').filter(Boolean) || [];
    provider.hideOutOfStock = searchSettings?.filters_hide_out_of_stock || false;
    provider.sortOptions = parseSortOptions(searchSettings?.sort_options);

    // Initialize filters from URL parameters
    const urlFilters = getInitialFiltersFromURL();
    if (Object.keys(urlFilters).length > 0) {
      // Convert URL filters to GraphQL format for processing
      provider.currentFilters = convertFiltersObjectToGraphQL(urlFilters);

      // Dispatch initial filters to update components
      window.dispatchEvent(
        new CustomEvent('searchrequest-current-filters-updated', {
          detail: {
            filters: urlFilters // Send simplified format for display
          }
        })
      );
      // Trigger initial search based on URL filters
      provider.type = searchType;
      if (searchType === 'plp') {
        if (provider.currentCollection){

          makeCollectionGraphRequest();
        }
      } else {
        makeSearchGraphRequest();
      }
    }

    // Detect current collection from URL or settings
    if (searchSettings?.collection?.handle) {
      provider.currentCollection = searchSettings.collection.handle;
    } else {
      // Try to detect collection from URL path
      const pathSegments = window.location.pathname.split('/');
      const collectionsIndex = pathSegments.indexOf('collections');
      if (collectionsIndex !== -1 && pathSegments[collectionsIndex + 1]) {
        provider.currentCollection = pathSegments[collectionsIndex + 1];
      } else {
        provider.currentCollection = 'all'; // Default fallback
      }
    }

    provider.initialized = true;
    // Initialize and trigger initial search for search pages
    if (searchType === 'search' || searchType === 'header-search') {
      const urlParams = new URLSearchParams(window.location.search);
      provider.searchQuery = urlParams.get('q');
      if (provider.searchQuery) {
        makeSearchGraphRequest();
      }
    }

    // Replace original calls with debounced versions
    provider.debouncedSearchRequest = debounce(async (detail) => {
      const { query, type, collection, collectionSize, productsPerPage, filters, page, limit, paginationType, } = detail;

      // Strict lock: do not start a new request if one is already processing
      if (globalRequestState.isProcessing(type)) {
        console.warn(`[ShopifySearchProvider] Debounced request ignored: another ${type} request is still processing`);
        return;
      }
      provider.type = type;

      if (type === 'plp') {
        provider.currentCollection = collection;
        provider.currentCollectionSize = collectionSize;
        provider.currentPage = page || 1;
        provider.productsPerPage = productsPerPage || provider.productsPerPage;
        provider.currentFilters = filters || provider.currentFilters || [];
        await makeCollectionGraphRequest(false, paginationType);

      } else if (type === 'header-search') {
        provider.previousSearchQuery = provider.searchQuery;
        provider.searchQuery = query;
        provider.limit = limit;
        // provider.currentCollectionSize = collectionSize;
        // provider.currentFilters = filters || provider.currentFilters || [];
        await makePredictiveGraphRequest();
      } else {
        provider.previousSearchQuery = provider.searchQuery;
        provider.searchQuery = query;
        provider.currentCollectionSize = collectionSize;
        provider.currentFilters = filters || provider.currentFilters || [];
        window.dispatchEvent(
          new CustomEvent('searchrequest-current-filters-updated', {
            detail: {
              filters: provider.currentFilters
            }
          })
        );
        await makeSearchGraphRequest();
      }

      provider.isProcessingRequest = false;
    }, 300);

    // Set up event listeners only once globally
    if (!globalProviderState.eventListenersSet) {
      window.addEventListener('searchrequest', handleSearchRequest);
      window.addEventListener('searchrequest-sortchanged', handleSortChange);
      window.addEventListener('searchrequest-filter-applied', handleFilterApplied);
      window.addEventListener('searchrequest-nextpage', handleNextPage);
      window.addEventListener('search-cleared', handleSearchCleared);
      globalProviderState.eventListenersSet = true;

      // Signal that the search provider is ready
      window._searchProviderReady = true;
      // console.log('ShopifySearchProvider: Event listeners set up successfully, provider ready');
    } else {
      console.debug('ShopifySearchProvider: Event listeners already set up');
    }
    // No-op cleanup: listeners are global and only removed on full page unload
    return () => {};
  }, []);

  // Handle search requests
  const handleSearchRequest = useCallback((event) => {
    // Use the debounced search function instead of processing immediately
    if (provider.debouncedSearchRequest) {
      provider.debouncedSearchRequest(event.detail);
    }
  }, []);

  // Handle sort changes
  const handleSortChange = useCallback(async (event) => {
    const { sortOption } = event.detail;

    provider.type = event.detail.type || 'plp';
    provider.isSortingActive = true;

    provider.isProcessingRequest = true;

    let sort = 'MANUAL';
    let direction = 'ASC';

    switch (sortOption) {
      case 'Newest':
        sort = 'CREATED';
        direction = 'ASC';
        break;
      case 'Oldest':
        sort = 'CREATED';
        direction = 'DESC';
        break;
      case 'Popular':
        sort = 'BEST_SELLING';
        break;
      case 'TitleAZ':
        sort = 'TITLE';
        direction = 'ASC';
        break;
      case 'TitleZA':
        sort = 'TITLE';
        direction = 'DESC';
        break;
      case 'Featured':
        sort = 'MANUAL';
        direction = 'ASC';
        break;
      case 'HighToLow':
        sort = 'PRICE';
        direction = 'DESC';
        break;
      case 'LowToHigh':
        sort = 'PRICE';
        direction = 'ASC';
        break;
      case 'Relevance':
        sort = 'RELEVANCE';
        direction = 'DESC';
        break;
    }

    if (provider.type === 'search' || provider.type === 'header-search') {
      if (sort !== 'RELEVANCE' && sort !== 'PRICE') {
        console.warn(`Search API supports only RELEVANCE or PRICE; defaulting to RELEVANCE.`);
        sort = 'RELEVANCE';
        direction = 'DESC';
      }
    }

    provider.currentSort = sort;
    provider.currentDirection = direction;
    provider.previousSort = sort;
    provider.previousDirection = direction;
    provider.currentPage = 1;
    provider.lastCursor = null;
    provider.pageCursors = {};

    if (provider.type === 'search' || provider.type === 'header-search') {
      await makeSearchGraphRequest();
    } else {
      await makeCollectionGraphRequest();
    }

    provider.isProcessingRequest = false;
  }, []);

  // Handle filter changes
  const handleFilterApplied = useCallback(async (event) => {
    const eventDetail = event.detail;

    // Strict lock: do not start a new request if one is already processing
    if (globalRequestState.isProcessing(provider.type)) {
      console.warn(
        `[ShopifySearchProvider] Filter request ignored: another ${provider.type} request is still processing`
      );
      return;
    }
    // Enforce global request state: only one request of a type can be active
    if (globalRequestState.activeRequests[provider.type]) {
      globalRequestState.activeRequests[provider.type].abort();
    }
    provider.isProcessingRequest = true;

    let filters = [];
    let filtersObject = {};

    // Handle different event formats for backward compatibility
    if (eventDetail.filters_object) {
      // New format: filters_object from collection filtering
      const { filters_object, type } = eventDetail;
      filtersObject = filters_object;

      if (filters_object) {
        Object.entries(filters_object).forEach(([key, values]) => {
          if (Array.isArray(values)) {
            values.forEach((value) => {
              try {
                // Try to parse as JSON first (for new API format with input field)
                const parsedInput = JSON.parse(value);
                if (parsedInput && typeof parsedInput === 'object') {
                  // This is the new format with input field
                  filters.push(parsedInput);
                } else {
                  // Fallback to old format
                  handleLegacyFilter(key, value, filters);
                }
              } catch (e) {
                // Not JSON, handle as legacy format
                handleLegacyFilter(key, value, filters);
              }
            });
          }
        });
      }
    } else if (eventDetail.input) {
      // Old format: individual input/applying from header search
      const { input, applying, type } = eventDetail;

      // Convert individual filter to filters array format
      if (applying) {
        filters = [...provider.currentFilters, input];
      } else {
        filters = provider.currentFilters.filter((filter) => filter !== input);
      }

      // Convert filters array back to filters_object format for URL sync
      // This is a simplified conversion - we'll treat each filter as its own key
      filtersObject = {};
      filters.forEach((filter, index) => {
        // Try to determine the filter key from the filter content
        let filterKey = `filter_${index}`;
        try {
          const parsed = JSON.parse(filter);
          if (parsed.taxonomyMetafield) {
            filterKey = parsed.taxonomyMetafield.key || filterKey;
          } else if (parsed.productMetafield) {
            filterKey = parsed.productMetafield.key || filterKey;
          } else if (parsed.variantOption) {
            filterKey = parsed.variantOption.key || filterKey;
          }
        } catch (e) {
          // Use default key
        }

        if (!filtersObject[filterKey]) {
          filtersObject[filterKey] = [];
        }
        filtersObject[filterKey].push(filter);
      });
    }

    // Helper function to handle legacy filter format
    function handleLegacyFilter(key, value, filters) {
      if (key === 'Room') {
        const filter = { tag: value };
        filters.push(filter);
      } else if (key === 'Category') {
        const filter = {
          productMetafield: {
            namespace: 'shopify',
            key: 'category',
            value: value
          }
        };
        filters.push(filter);
      } else if (key === 'Price' || key === 'price' || key === 'filter.v.price' || key.includes('price')) {
        try {
          if (Array.isArray(value)) {
            const [min, max] = value[0].split('-');
            filters.push({ price: { min: parseFloat(min), max: parseFloat(max) } });
          } else {
            const [min, max] = value.split('-');
            filters.push({ price: { min: parseFloat(min), max: parseFloat(max) } });
          }
        } catch (e) {
          console.error(`ShopifySearchProvider: Failed to parse price filter "${value}":`, e);
        }
      } else if (key === 'Brand') {
        const filter = { productVendor: value };
        filters.push(filter);
      } else if (key === 'Product type') {
        const filter = { productType: value };
        filters.push(filter);
      } else if (key === 'Availability') {
        const filter = {
          available: value === 'In stock'
        };
        filters.push(filter);
      } else if (key === 'available') {
        const filter = {
          available: value === 'true' || value === true
        };
        filters.push(filter);
      } else if (key.startsWith('named_tags.')) {
        const tagPrefix = key.replace('named_tags.', '');
        const filter = { tag: `${tagPrefix}:${value}` };
        filters.push(filter);
      } else if (key.startsWith('options.')) {
        const optionName = key.replace('options.', '');
        const filter = {
          variantOption: { name: optionName, value: value }
        };
        filters.push(filter);
      } else if (key === 'Variant' || key.includes('option.')) {
        const optionName = key.split('.').pop();
        const filter = {
          variantOption: { name: optionName, value: value }
        };
        filters.push(filter);
      } else if (key.includes('p.t.')) {
        const filter = {
          category: { id: value }
        };
        filters.push(filter);
      } else if (key === 'tags') {
        const filter = { tag: value };
        filters.push(filter);
      } else {
        // Generic fallback for any other filter type

        // For most filters, try to use them as product metafields first
        if (key.includes('_') || key.includes('.')) {
          // Looks like a metafield key (e.g., "colour_name", "bottle_colour", etc.)

          let namespace = key.split('.')[3] || 'info'; // Use fourth part as namespace
          let metafieldKey = key.split('.').pop(); // Use last part as key

          let filter = {};
          if (key.includes('shopify')) {
            namespace = key.split('.')[5] || 'info'; // Use fourth part as namespace
            console.warn(
              `ShopifySearchProvider: Detected 'shopify' namespace in key "${key}", using default namespace.`
            );
            filter = {
              taxonomyMetafield: {
                namespace: 'shopify',
                key: metafieldKey,
                value: value // This should be the GID value for taxonomyMetafield
              }
            };
          } else {
            filter = {
              productMetafield: {
                namespace: namespace,
                key: metafieldKey,
                value: value
              }
            };
          }
          filters.push(filter);
        } else {
          // Last fallback: use as tag
          const filter = { tag: value };
          filters.push(filter);
        }
      }
    }

    // If filters are being cleared, restore previous sort/direction and ensure filters are empty
    if (filters.length === 0) {
      provider.currentSort = provider.previousSort || provider.currentSort;
      provider.currentDirection = provider.previousDirection || provider.currentDirection;
      provider.currentFilters = [];
    } else {
      provider.currentFilters = filters;
    }
    // Always reset to first page when filtering
    provider.currentPage = 1;
    provider.lastCursor = null; // Reset cursor
    provider.pageCursors = {}; // Reset page cursors

    // Immediately dispatch an empty product list to clear UI before new results
    if (provider.type !== 'header-search') {
      // window.dispatchEvent(
      //   new CustomEvent('searchrequest-products-complete', {
      //     detail: {
      //       type: provider.type,
      //       page: provider.currentPage,
      //       products: [],
      //       totalResults: 0,
      //       query: provider.searchQuery,
      //       filters: []
      //     }
      //   })
      // );

      // Update URL with current filters
      updateURLWithFilters(filtersObject || {});
    }

    // Dispatch current filters updated event
    window.dispatchEvent(
      new CustomEvent('searchrequest-current-filters-updated', {
        detail: {
          filters: filtersObject || {}
        }
      })
    );

    // Determine the type from event detail
    const type = eventDetail.type;
    const triggerSearch = eventDetail.triggerSearch ?? true;
    provider.type = type;

    if (provider.searchQuery && (type === 'search' || type === 'header-search') && triggerSearch) {
      await makeSearchGraphRequest();
    } else {
      updateURLWithFilters(filtersObject || {});

      await makeCollectionGraphRequest();
    }

    provider.isProcessingRequest = false;
  }, []);

  // Handle pagination requests
  const handleNextPage = useCallback(async (event) => {
    const { page, paginationType } = event.detail;

    // Strict lock: do not start a new request if one is already processing
    if (globalRequestState.isProcessing(provider.type)) {
      console.warn(
        `[ShopifySearchProvider] NextPage request ignored: another ${provider.type} request is still processing`
      );
      return;
    }
    // Enforce global request state: only one request of a type can be active
    if (globalRequestState.activeRequests[provider.type]) {
      globalRequestState.activeRequests[provider.type].abort();
    }
    provider.isProcessingRequest = true;

    provider.currentPage = page;

    if (provider.searchQuery) {
      // For search, track page cursors similar to collection
      let afterCursor = null;

      // console.log('Page cursors', provider.pageCursors);
      // Always use the correct cursor for the requested page
      // if (page > 1) {
        afterCursor = provider.pageCursors[page - 1] || null;
        // console.log('[ShopifySearchProvider] Using search cursor for page', page, ':', afterCursor);
      // }

      // Save currentPage before request
      provider.currentPage = page;

      const searchResults = await fetchSearchResults(
        provider.searchQuery,
        provider.productsPerPage,
        afterCursor,
        provider.currentFilters,
        provider.currentSort === 'MANUAL' ? 'RELEVANCE' : provider.currentSort,
        provider.currentDirection
      );

        if (!searchResults) {
          return;
        }

      // Store cursor for the current page and update lastCursor for next page
      if (searchResults?.pageInfo?.hasNextPage) {
          provider.pageCursors[provider.currentPage] = searchResults.pageInfo.endCursor;
          provider.lastCursor = searchResults.pageInfo.endCursor;
          // Always set pageCursors[1] after initial search so page 2 can use it
          if (provider.currentPage === 1 && searchResults.pageInfo.endCursor) {
            provider.pageCursors[1] = searchResults.pageInfo.endCursor;
          }
      } else {
        provider.lastCursor = null;
      }

      // Extract product nodes
      const productNodes = (searchResults.products || [])
        .filter((product) => product.node.id.includes('Product'))
        .map((product) => shopify.transformGraphqlProduct(product.node));

      // Dispatch filter facets for search
      dispatchFilterFacets(provider.type, searchResults.filters || []);

      // Dispatch search results event
      window.dispatchEvent(
        new CustomEvent('searchrequest-products-complete', {
          detail: {
            type: provider.type,
            page: provider.currentPage,
            products: productNodes,
            totalResults: searchResults.totalCount || 0, // Ensure totalCount is passed
            query: provider.searchQuery,
            filters: searchResults.filters || []
          }
        })
      );
    } else {
      await makeCollectionGraphRequest(false, paginationType);
    }

    provider.isProcessingRequest = false;
  }, []);

  // Fetch products from collection with GraphQL
  const fetchProductsFromCollection = useCallback(
    async (
      handle,
      first,
      after = null,
      filters = [],
      sort = provider.currentSort,
      direction = provider.currentDirection,
      useSimple = false
    ) => {
      window.dispatchEvent(
        new CustomEvent('show-spinner', {
          detail: {
            id: 'spinner-collection'
          }
        })
      );

      const endpoint = api.getStorefrontGraphqlBaseUrl();
      const accessToken = provider.apiKey;

      // GraphQL query for collections
      const query = useSimple ? COLLECTION_SIMPLE_QUERY.loc.source.body : COLLECTION_QUERY.loc.source.body;

      // Convert direction to GraphQL reverse boolean: true for DESC, false for ASC
      const directionBoolean = direction === 'DESC';

      // Get metafield identifiers from window object
      const metafieldIdentifiers = window.productMetafieldIdentifiers || [];

      const variables = {
        handle: handle,
        first: first,
        after: after, // Use the cursor for pagination
        sort: sort,
        direction: directionBoolean,
        filters: filters,
        metafieldIdentifiers: metafieldIdentifiers
      };

      // Execute the query
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': accessToken
          },
          body: JSON.stringify({
            query: query,
            variables: variables
          })
        });

        if (!response.ok) {
          throw new Error(`GraphQL request failed: ${response.statusText}`);
        }

        const result = await response.json();
        const collectionData = result?.data?.collectionByHandle?.products;

        // Fix: Return the correct structure
        return {
          products: collectionData?.edges?.map((edge) => edge.node) || [],
          filters: collectionData?.filters || [],
          pageInfo: collectionData?.pageInfo || {},
          totalCount: collectionData?.edges?.length || 0
        };
      } catch (error) {
        console.error('Error fetching products from collection:', error);
        return null;
      }
    },
    []
  );

  // Fetch products from search with GraphQL
  const fetchSearchResults = useCallback(
    async (searchTerm, first, after = null, filters = [], sort = 'RELEVANCE', direction = 'ASC') => {
      window.dispatchEvent(
        new CustomEvent('show-spinner', {
          detail: {
            id: 'spinner-collection'
          }
        })
      );

      const endpoint = api.getStorefrontGraphqlBaseUrl();
      const accessToken = provider.apiKey;

      // GraphQL query for search
      const query = SEARCH_QUERY.loc.source.body;

      // Convert direction to GraphQL reverse boolean: true for DESC, false for ASC
      const reverse = direction === 'DESC';

      // Use productsPerPage from provider state if not provided
      const perPage = first || provider.productsPerPage || 24;
      // Ensure filters is always an array
      const safeFilters = Array.isArray(filters) ? filters : [];
      // Only pass supported sort keys for search
      let searchSort = sort;
      let searchReverse = reverse;
      if (searchSort !== 'RELEVANCE' && searchSort !== 'PRICE') {
        searchSort = 'RELEVANCE';
        searchReverse = true;
      }

      // Get metafield identifiers from window object
      const metafieldIdentifiers = window.productMetafieldIdentifiers || [];

      const variables = {
        query: searchTerm,
        first: perPage,
        after: after, // Use the cursor for pagination
        sort: searchSort,
        reverse: searchReverse,
        productFilters: safeFilters.map((filter) => (typeof filter === 'string' ? JSON.parse(filter) : filter)),
        metafieldIdentifiers: metafieldIdentifiers,
        types: ['PRODUCT']
      };

      // Execute the query
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': accessToken
          },
          body: JSON.stringify({
            query: query,
            variables: variables
          })
        });

        if (!response.ok) {
          throw new Error(`GraphQL request failed: ${response.statusText}`);
        }

        const {
          data: { search }
        } = await response.json();

        return {
          products: search?.edges || [],
          filters: search?.filters || [],
          pageInfo: search?.pageInfo || {},
          totalCount: search?.totalCount || 0
        };
      } catch (error) {
        console.error('Error fetching search results:', error);
        return null;
      }
    },
    []
  );

  // Fetch products from search with GraphQL
  const fetchPredictiveSearchResults = useCallback(async (searchTerm, limit=null) => {
    const endpoint = api.getStorefrontGraphqlBaseUrl();
    const accessToken = provider.apiKey;

    // GraphQL query for search
    const query = PREDICTIVE_SEARCH_QUERY.loc.source.body;

    // Get metafield identifiers from window object
    const metafieldIdentifiers = window.productMetafieldIdentifiers || [];

    const variables = {
      query: searchTerm,
      metafieldIdentifiers,
      // limit, // TODO: Handle limit for product results, but this is for total results across all types
    };

    // Execute the query
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': accessToken
        },
        body: JSON.stringify({
          query: query,
          variables: variables
        })
      });

      if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.statusText}`);
      }

      const {
        data: { predictiveSearch }
      } = await response.json();

      const transformedProducts = predictiveSearch.products?.map((product) => shopify.transformGraphqlProduct(product)) || [];

      return {
        articles: predictiveSearch?.articles || [],
        collections: predictiveSearch?.collections || [],
        pages: predictiveSearch?.pages || [],
        products: transformedProducts,
        queries: predictiveSearch?.queries || []
      };
    } catch (error) {
      console.error('Error fetching predictive search results', error);
      return null;
    }
  }, []);

  // Process collection results and dispatch events
  const makeCollectionGraphRequest = useCallback(
    async (forceSequentialLoad = false, paginationType = 'pages') => {
      // Strict lock: do not start a new request if one is already processing

      try {
        const targetPage = provider.currentPage;
        let needsSequentialLoad = forceSequentialLoad;

        if (targetPage > 1 && !needsSequentialLoad) {
          const previousPageCursor = provider.pageCursors[targetPage - 1];
          if (!previousPageCursor) {
            needsSequentialLoad = true;
            console.log('[ShopifySearchProvider] Sequential load needed - missing cursor for page', targetPage - 1);
          }
        }

        if (needsSequentialLoad && targetPage > 1) {
          let startPage = 1;
          for (let i = targetPage - 1; i >= 1; i--) {
            if (provider.pageCursors[i]) {
              startPage = i + 1;
              break;
            }
          }

          for (let page = startPage; page <= targetPage; page++) {
            let afterCursor = null;
            if (page > 1) {
              afterCursor = provider.pageCursors[page - 1] || null;
            }

            const responseData = await fetchProductsFromCollection(
              provider.currentCollection,
              provider.productsPerPage,
              afterCursor,
              provider.currentFilters,
              provider.currentSort,
              provider.currentDirection
            );

            const productsData = responseData.products;
            if (!productsData) {
              console.error('[ShopifySearchProvider] Failed to load page', page);
              return;
            }

            if (productsData.pageInfo?.endCursor) {
              provider.pageCursors[page] = productsData.pageInfo.endCursor;
            }
            
            // Calculate paginationTotal using the shared function
            if (page === targetPage) {
              const productNodes = responseData.products?.map((product) => shopify.transformGraphqlProduct(product)) || [];

              // Update lastCursor for next page navigation
              if (responseData.pageInfo?.hasNextPage) {
                provider.lastCursor = responseData.pageInfo.endCursor;
              } else {
                provider.lastCursor = null;
              }

              // Calculate paginationTotal using the shared function
              const paginationTotal = await calculatePaginationTotal(
                responseData,
                provider.currentFilters,
                provider.currentCollectionSize,
                fetchProductsFromCollection,
                provider.currentCollection,
                provider.currentSort,
                provider.currentDirection,
                'collection'
              );

              dispatchFilterFacets('plp', responseData.filters || []);
              window.dispatchEvent(
                new CustomEvent('product-list', {
                  detail: {
                    type: 'plp',
                    page: targetPage,
                    products: productNodes,
                    totalResults: paginationTotal,
                    paginationType: paginationType,
                    hasNextPage: responseData.pageInfo?.hasNextPage || false,
                    hasPreviousPage: responseData.pageInfo?.hasPreviousPage || false,
                    sequentialLoad: true,
                    isTargetPage: true,
                    isSorting: provider.isSortingActive
                  }
                })
              );

              // Hide spinner after products are loaded
              window.dispatchEvent(
                new CustomEvent('hide-spinner', {
                  detail: {
                    id: 'spinner-collection',
                    context: 'collection-products-loaded'
                  }
                })
              );

              // Reset sorting flag after results are dispatched
              provider.isSortingActive = false;
            }
          }
        } else {
          // Direct page navigation (page 1 or adjacent page with cursor available)
          let afterCursor = null;
          if (targetPage > 1) {
            afterCursor = provider.pageCursors[targetPage - 1] || null;
          }

          const responseData = await fetchProductsFromCollection(
            provider.currentCollection,
            provider.productsPerPage,
            afterCursor,
            provider.currentFilters,
            provider.currentSort,
            provider.currentDirection
          );

          const productsData = responseData;
          if (!productsData) return;

          const productNodes = productsData.products?.map((product) => shopify.transformGraphqlProduct(product)) || [];

          if (productsData.pageInfo?.hasNextPage) {
            provider.pageCursors[provider.currentPage] = productsData.pageInfo.endCursor;
            provider.lastCursor = productsData.pageInfo.endCursor;
          } else {
            provider.lastCursor = null;
          }

          // Calculate paginationTotal using the shared function
          const paginationTotal = await calculatePaginationTotal(
            responseData,
            provider.currentFilters,
            provider.currentCollectionSize,
            fetchProductsFromCollection,
            provider.currentCollection,
            provider.currentSort,
            provider.currentDirection,
            'collection'
          );

          dispatchFilterFacets('plp', responseData.filters || []);
          window.dispatchEvent(
            new CustomEvent('product-list', {
              detail: {
                type: 'plp',
                page: provider.currentPage,
                products: productNodes,
                totalResults: paginationTotal,
                paginationType: paginationType,
                hasNextPage: responseData.pageInfo?.hasNextPage || false,
                hasPreviousPage: responseData.pageInfo?.hasPreviousPage || false,
                sequentialLoad: false,
                isSorting: provider.isSortingActive,
                isTargetPage: true
              }
            })
          );

          // Hide spinner after products are loaded
          window.dispatchEvent(
            new CustomEvent('hide-spinner', {
              detail: {
                id: 'spinner-collection',
                context: 'collection-products-loaded'
              }
            })
          );
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Request was cancelled');
        } else {
          console.error('Error in makeCollectionGraphRequest:', error);
        }
      } finally {
        globalRequestState.endRequest('collection');
      }
    },
    [fetchProductsFromCollection]
  );

  const makePredictiveGraphRequest = useCallback(async () => {
    const predictiveSearchResults = await fetchPredictiveSearchResults(provider.searchQuery, provider.limit);
    // console.debug('predictiveSearchResults', predictiveSearchResults);

    if (!predictiveSearchResults) throw new Error('No predictive search results returned');

    const {
      articles,
      collections,
      pages,
      products,
      queries
    } = predictiveSearchResults;

    window.dispatchEvent(
      new CustomEvent('searchrequest-products-complete', {
        detail: {
          type: provider.type,
          products,
          query: provider.searchQuery,
        }
      })
    );

    window.dispatchEvent(
      new CustomEvent('searchrequest-collections-complete', {
        detail: {
          collections,
          query: provider.searchQuery,
        }
      })
    );
  });

  // Process search results and dispatch events
  const makeSearchGraphRequest = useCallback(async () => {
    try {
      // Always use provider.currentSort/currentDirection for search requests
      const searchResults = await fetchSearchResults(
        provider.searchQuery,
        provider.productsPerPage,
        provider.lastCursor,
        provider.currentFilters,
        provider.currentSort === 'MANUAL' ? 'RELEVANCE' : provider.currentSort,
        provider.currentDirection
      );

      // console.log('[ShopifySearchProvider] Search results received:', searchResults);

      if (!searchResults) throw new Error('No search results returned');

      const {
        products,
        filters,
        pageInfo,
        totalCount
      } = searchResults;

      // console.log('[ShopifySearchProvider] Products from searchResults:', products);
      // console.log('[ShopifySearchProvider] Total count:', totalCount);

      // On initial search (page 1), capture and log the endCursor for pagination
      if (provider.currentPage === 1 && pageInfo?.hasNextPage && pageInfo?.endCursor) {
        provider.pageCursors[1] = pageInfo.endCursor;
        console.log('[ShopifySearchProvider] Initial search endCursor captured for page 1:', pageInfo.endCursor);
      }

      // Extract product nodes and the last cursor
      const productNodes = products
        ?.filter((product) => product?.node?.id?.includes('Product'))
        .map((product) => shopify.transformGraphqlProduct(product.node));

      // console.log('[ShopifySearchProvider] Product nodes after transformation:', productNodes);
      // console.log('[ShopifySearchProvider] Product nodes length:', productNodes?.length || 0);

      provider.lastCursor = pageInfo?.endCursor;

      // Hide spinner
      window.dispatchEvent(
        new CustomEvent('hide-spinner', {
          detail: {
            id: provider.type === 'header' ? 'spinner-header-search' : 'spinner-search'
          }
        })
      );

      // Dispatch filter facets for search
      dispatchFilterFacets(provider.type, filters || []);

      // Use searchrequest-products-complete event for search results
      window.dispatchEvent(
        new CustomEvent('searchrequest-products-complete', {
          detail: {
            type: provider.type,
            page: provider.currentPage,
            products: productNodes,
            totalResults: totalCount || 0,
            query: provider.searchQuery,
            filters: filters || []
          }
        })
      );

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Request was cancelled');
      } else {
        console.error('Error in makeSearchGraphRequest:', error);
      }
    } finally {
      globalRequestState.endRequest('search');
    }
  }, [fetchSearchResults]);

  const handleSearchCleared = useCallback(() => {
    // console.debug('Search cleared event received');
    provider.searchQuery = '';
    provider.currentFilters = [];
    provider.currentPage = 1;
    provider.lastCursor = null;
    provider.pageCursors = {};
  }, []);

  /**
   * Calculate pagination total based on active filters and response data
   * @param {Object} responseData - The response data containing filters and products
   * @param {Array} currentFilters - The currently active filters
   * @param {number} currentCollectionSize - The current collection size
   * @param {Function} fetchProductsFromCollection - Function to fetch products (for fallback count)
   * @param {string} currentCollection - Current collection handle
   * @param {string} currentSort - Current sort option
   * @param {string} currentDirection - Current sort direction
   * @param {string} context - Context type ('collection' or 'search')
   * @returns {Promise<number>} The calculated pagination total
   */
  async function calculatePaginationTotal(
    responseData, 
    currentFilters, 
    currentCollectionSize, 
    fetchProductsFromCollection, 
    currentCollection, 
    currentSort, 
    currentDirection,
    context = 'collection'
  ) {
    // For search context, always use the totalCount from API response directly
    if (context === 'search') {
      return responseData.totalCount || 0;
    }

    let paginationTotal = currentCollectionSize;
    const productsCount = responseData.products?.length || 0;

    // For collections with filters, use batched simple queries to get accurate count
    if (currentCollection && fetchProductsFromCollection) {
      let totalProducts = 0;
      let afterCursor = null;
      let hasNextPage = true;

      // Batch through all products with simple queries (max 250 per batch)
      while (hasNextPage) {
        const pageData = await fetchProductsFromCollection(
          currentCollection,
          250, // Max products per batch
          afterCursor,
          currentFilters,
          currentSort,
          currentDirection,
          true // Use simple query
        );

        if (!pageData || !Array.isArray(pageData.products)) break;

        totalProducts += pageData.products.length;
        hasNextPage = pageData.pageInfo?.hasNextPage;
        afterCursor = pageData.pageInfo?.endCursor || null;
      }

      return totalProducts;
    }

    // For collections without filters, use existing logic based on response data
    if (currentFilters.length > 0 && Array.isArray(responseData.filters)) {
      // Group active filters by type
      const typeGroups = {};
      currentFilters.forEach(activeFilter => {
        const type = activeFilter.productMetafield?.key || activeFilter.taxonomyMetafield?.key || activeFilter.key || 'unknown';
        if (!typeGroups[type]) typeGroups[type] = [];
        typeGroups[type].push(activeFilter);
      });

      // For each type, sum the counts of all matching filters
      const typeCounts = Object.values(typeGroups).map(group => {
        let sum = 0;
        group.forEach(activeFilter => {
          responseData.filters.forEach(filterInResponse => {
            const matchLabel = activeFilter.productMetafield?.value || activeFilter.taxonomyMetafield?.value || activeFilter.value;
            if (!activeFilter.taxonomyMetafield?.value) {
              if (!matchLabel) return;
              const matchedValue = filterInResponse.values.find(value => value.label === matchLabel);
              if (matchedValue) {
                sum += matchedValue.count || 0;
              }
            } else {
              filterInResponse.values.forEach(value => {
                const parsedJSON = JSON.parse(value.input || '{}');
                if (parsedJSON.taxonomyMetafield?.value === activeFilter.taxonomyMetafield?.value) {
                  sum += parsedJSON.taxonomyMetafield?.count || value.count || 0;
                }
              });
            }
          });
        });
        return sum;
      });

      // If more than one type, use the minimum sum; otherwise, use the sum
      if (typeCounts.length > 1) {
        paginationTotal = Math.min(...typeCounts);
      } else if (typeCounts.length === 1) {
        paginationTotal = typeCounts[0];
      } else {
        paginationTotal = currentCollectionSize;
      }

    } else if (Array.isArray(responseData.filters)) {
      // No active filters, sum all counts from all filter values
      paginationTotal = responseData.filters.reduce((sum, filter) => {
        const filterSum = Array.isArray(filter.values)
          ? filter.values.reduce((vSum, value) => vSum + (value.count || 0), 0)
          : 0;
        return sum + filterSum;
      }, 0);
    }

    return paginationTotal;
  }

  /**
   * Dispatch filter facets event
   * @param {string} type - The type of the event (e.g., 'plp', 'search')
   * @param {Array} apiFilters - The filters array from the API
   */
  function dispatchFilterFacets(type, apiFilters) {
    const filterValueMapping = createFilterValueMapping(apiFilters);
    const transformedFacets = apiFilters.map((filter) => ({
      id: filter.id,
      key: filter.id,
      label: filter.label,
      type: filter.type,
      values: filter.values.map((value) => ({
        id: value.id,
        key: value.input,
        label: value.label,
        count: value.count,
        input: value.input,
        swatch: value.swatch
      }))
    }));

    window.dispatchEvent(
      new CustomEvent('filter-list', {
        detail: { type, filters: transformedFacets, filterValueMapping }
      })
    );
  }

  /**
   * Helper function to create filter value mapping from API filters
   * This creates a mapping between GID values and their display labels
   * @param {Array} apiFilters - The filters array from API
   * @returns {Map} Map of GID values to display labels
   */
  function createFilterValueMapping(apiFilters) {
    const mapping = new Map();

    if (!apiFilters || !Array.isArray(apiFilters)) return mapping;

    apiFilters.forEach((filter) => {
      if (filter.values && Array.isArray(filter.values)) {
        filter.values.forEach((value) => {
          mapping.set(value.id, value.label);
        });
      }
    });

    return mapping;
  }

  // Create the provider object that our base SearchProvider will use
  const shopifyProvider = {
    initialize,
    handleSearchRequest,
    handleSortChange,
    handleFilterApplied,
    handleNextPage
  };

  return (
    <SearchProvider
      provider={shopifyProvider}
      settings={settings}
      searchType={searchType}
    >
      {children}
    </SearchProvider>
  );
}

export default ShopifySearchProvider;

// Define a global AbortController for managing request cancellations
const globalRequestController = {
  controller: null,
  reset() {
    if (this.controller) {
      this.controller.abort(); // Cancel the current request
    }
    this.controller = new AbortController(); // Create a new controller
    return this.controller.signal;
  }
};

// Define a global state for managing active requests by type
const globalRequestState = {
  activeRequests: {},
  processing: {}, // global lock for request types
  startRequest(type) {
    if (this.activeRequests[type]) {
      this.activeRequests[type].abort(); // Abort the current request of this type
    }
    this.processing[type] = true;
    const controller = new AbortController();
    this.activeRequests[type] = controller;
    return controller.signal;
  },
  endRequest(type) {
    delete this.activeRequests[type];
    this.processing[type] = false;
  },
  isProcessing(type) {
    return !!this.processing[type];
  }
};